import React from 'react';
import { Icon } from '@workspace/design-system/src/components';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    // 페이지 번호 생성 로직
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const siblingCount = 2; // 현재 페이지 양옆에 보여줄 페이지 수
        const totalPageNumbers = siblingCount * 2 + 1; // 현재 페이지 포함 총 5개

        // 총 페이지가 충분히 적으면 모두 표시 (1 + 5 + 1 + 1 = 8개)
        if (totalPages <= totalPageNumbers + 2) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // 항상 첫 페이지 표시
            pages.push(1);

            // 현재 페이지 기준으로 범위 계산 (±2)
            let startPage = Math.max(2, currentPage - siblingCount);
            let endPage = Math.min(totalPages - 1, currentPage + siblingCount);

            // 왼쪽 끝에 있을 때 오른쪽으로 확장
            if (currentPage <= siblingCount + 1) {
                endPage = Math.min(totalPageNumbers, totalPages - 1);
            }

            // 오른쪽 끝에 있을 때 왼쪽으로 확장
            if (currentPage >= totalPages - siblingCount) {
                startPage = Math.max(2, totalPages - totalPageNumbers);
            }

            // 왼쪽 ... 표시 여부
            const showLeftEllipsis = startPage > 2;
            if (showLeftEllipsis) {
                pages.push('left-ellipsis');
            }

            // 중간 페이지들 표시
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            // 오른쪽 ... 표시 여부
            const showRightEllipsis = endPage < totalPages - 1;
            if (showRightEllipsis) {
                pages.push('right-ellipsis');
            }

            // 항상 마지막 페이지 표시
            pages.push(totalPages);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            padding: '16px 8px'
        }}>
            {/* 왼쪽 화살표 */}
            <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                style={{
                    width: '16px',
                    height: '16px',
                    padding: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.3 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.2s ease'
                }}
            >
                <Icon name="ChevronLeft" size={16} color={currentPage === 1 ? '#BFBFBF' : '#595959'} />
            </button>

            {/* 페이지 번호들 */}
            {pageNumbers.map((page) => {
                if (typeof page === 'string') {
                    // ... (ellipsis) 표시
                    return (
                        <div
                            key={page}
                            style={{
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Icon name="Meatball" size={24} color="#595959" />
                        </div>
                    );
                }

                const isActive = page === currentPage;

                return (
                    <button
                        key={page}
                        onClick={() => onPageChange(page as number)}
                        style={{
                            width: '24px',
                            height: '24px',
                            padding: 0,
                            border: 'none',
                            backgroundColor: isActive ? '#e6f4ff' : 'transparent',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontFamily: 'Pretendard, sans-serif',
                            fontWeight: isActive ? 700 : 400,
                            color: isActive ? '#3c78f4' : '#595959',
                            lineHeight: isActive ? '16px' : '20px',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {page}
                    </button>
                );
            })}

            {/* 오른쪽 화살표 */}
            <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                style={{
                    width: '16px',
                    height: '16px',
                    padding: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.3 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.2s ease'
                }}
            >
                <Icon name="ChevronRight" size={16} color={currentPage === totalPages ? '#BFBFBF' : '#595959'} />
            </button>
        </div>
    );
}

